import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { BrandLogo, localized, useBranding } from '@/shared/branding';
import { PoweredBy } from '@/shared/components/PoweredBy';

export const Route = createFileRoute('/_auth')({
  component: AuthShell,
});

function AuthShell() {
  const { t, i18n } = useTranslation();
  const branding = useBranding();

  const highlights = [
    t('home.highlights.delegation', 'Resident delegation'),
    t('home.highlights.workers', 'Worker access'),
    t('home.highlights.secure', 'Secure compound entry'),
    t('home.highlights.audit', 'Full audit trail'),
  ];

  return (
    <div className="grid min-h-screen grid-cols-1 bg-canvas lg:grid-cols-2">
      {/* Brand pane (hidden on small screens) — a rich indigo→violet gradient
          mesh with floating glass panels gives the login real depth. */}
      <div className="relative hidden overflow-hidden p-12 text-white [background-image:var(--gradient-mesh)] lg:flex lg:flex-col lg:justify-between">
        {/* Depth: faint floating glass panels + a fine grain of noise. */}
        <div
          aria-hidden
          className="absolute -end-12 top-20 size-52 rotate-12 rounded-[2rem] border border-white/15 bg-white/5 backdrop-blur-md"
        />
        <div
          aria-hidden
          className="absolute end-32 top-72 size-32 -rotate-6 rounded-3xl border border-white/15 bg-white/5 backdrop-blur-md"
        />
        <div
          aria-hidden
          className="absolute -bottom-16 -start-10 size-72 rounded-full bg-white/5 blur-2xl"
        />

        {/* Wordmark — logo inverted to read light on the gradient. */}
        <div className="relative z-10">
          <BrandLogo className="max-h-14 w-auto max-w-[13rem] object-contain brightness-0 invert" />
        </div>

        {/* Hero copy + feature chips */}
        <div className="relative z-10 max-w-md">
          <h2 className="text-[2.5rem] font-semibold leading-[1.1] tracking-[-0.025em]">
            {t('home.title')}
          </h2>
          <p className="mt-5 text-base leading-relaxed text-white/75">{t('home.subtitle')}</p>
          <ul className="mt-9 flex flex-wrap gap-2.5">
            {highlights.map((h) => (
              <li
                key={String(h)}
                className="rounded-full border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white/95 backdrop-blur-md"
              >
                {h}
              </li>
            ))}
          </ul>
        </div>

        {/* Footer — copyright only. The single "Powered by SSS" credit lives in
            the form pane (always visible; this brand pane is hidden on mobile). */}
        <p className="relative z-10 text-xs text-white/55">
          © {localized(branding.name, i18n.language)}
        </p>
      </div>

      {/* Form pane ----------------------------------------------------- */}
      <div className="flex flex-col">
        {/* Mobile wordmark (brand pane is hidden < lg) */}
        <header className="px-6 py-6 lg:hidden">
          <BrandLogo className="max-h-11 w-auto max-w-[9rem] object-contain" />
        </header>

        <main className="flex flex-1 items-center justify-center px-6 py-10 lg:px-16">
          <div className="w-full max-w-sm">
            <Outlet />
          </div>
        </main>

        <footer className="flex flex-col items-center gap-2 px-6 pb-8 lg:items-start lg:px-16">
          <PoweredBy />
          <p className="text-xs text-foreground-subtle">
            © {new Date().getFullYear()} {branding.name.en}
          </p>
        </footer>
      </div>
    </div>
  );
}
